import * as Phaser from "phaser-ce"
import { COLOR } from "../../../common/Color"
import { ActivityKey } from "../../../shared/models/ActivityKey"
import { ButtonContainer, Buttons, ButtonSize, ButtonType } from "../../generic-sprites/Buttons"
import { loadGenericAssets } from "../../generic-sprites/GenericAssets"
import { Audio } from "../../utils/Audio"
import { ColorUtils } from "../../utils/ColorUtils"
import { PhaserText } from "../../utils/PhaserText"
import { PhaserUtils } from "../../utils/PhaserUtils"
import { Scale } from "../../utils/Scale"
import { Sprites } from "../../utils/Sprites"
import { Transitions } from "../transitions/Transitions"
import { EveryCigImages, EveryCigPart1Audio, loadAssets } from "./EveryCigAssets"
import { EveryCigStore } from "./EveryCigStore"
import { EveryCigSpriteType } from "./EveryCigTypes"
import { EveryCigBodyPartButtonContainer } from "./sprites/EveryCigBodyPartButtonContainer"
import { EveryCigDropdownScreenContainer } from "./sprites/EveryCigDropdownScreenContainer"
import { EveryCigSpriteCreator } from "./sprites/EveryCigSpriteCreator"
import { EveryCigVoteScreenOptionContainer } from "./sprites/EveryCigVoteScreenOptionContainer"
import { EveryCigWarningScreen } from "./sprites/EveryCigWarningScreen"


export class EveryCigState extends Phaser.State implements EveryCigStateShared{

    sprites: EveryCigSpriteCreator
    button: ButtonContainer
    buttons: EveryCigBodyPartButtonContainer

    async preload() {
        loadGenericAssets(this.game)
        loadAssets(this.game, 1)
    }

    async create() {
        EveryCigStore.newInstance()
        await initializeEveryCigStateShared(this)

        this.setContinueButton()

        // Debug
        // CCUtils.debug ? this.displayActivity() : this.introSequence()
        // Real
        this.introSequence()
    }

    private introSequence = () => {
        const warning = this.sprites.create(EveryCigSpriteType.WARNING_SCREEN, {
            x: Scale.x(0.491),
            y: Scale.y(-0.738)
        }) as EveryCigWarningScreen

        this.button.setOnClick(() => {
            warning.destroy()
            this.displayActivity()
        })

        Audio.add(this.game, EveryCigPart1Audio.WARNING, {
            onStop: () => this.button.enable()
        })
    }

    private displayActivity = () => {
        this.button.destroy()
        this.createVoteGrossestButton(this.voteGrossest)
        this.buttons = this.sprites.create(EveryCigSpriteType.BODY_PART_BUTTONS, {
            x: 0,
            y: 0,
            button: this.button
        }) as EveryCigBodyPartButtonContainer
        this.buttons.introSequence()
    }

    private voteGrossest = () => {
        this.button.destroy()
        this.createVoteGrossestButton(this.createVotingScreen, false)
        this.buttons.setNewButton(this.button)
        this.createVotingScreen()
    }

    private finalScreen = () => {
        this.button.disable()
        const screen = this.createDropdownScreen()
        const bullets = PhaserText.bulletList(this.game, {
            x: Scale.x(0.561),
            y: Scale.y(0.12),
            fontPx: 20,
            wordWrapWidth: 0.33,
            debug: true
        }, Scale.y(0.01))
        const onOpen = () => {
            bullets("Every Cigarette does some harm to your body.")
            bullets("The more you smoke, the more damage is done.")
            Audio.add(this.game, EveryCigPart1Audio.EVERY_CIG, {
                onStop: () => {
                    this.button.destroy()
                    const nextButton = Buttons.create(this.game, "Next", () => Transitions.startOutro(this.game, ActivityKey.EVERY_CIG), {
                        debug: true,
                        buttonType: ButtonType.PRIMARY,
                        buttonSize: ButtonSize.WIDE,
                        scale: 1.1,
                        x: Scale.x(0.624),
                        y: Scale.y(0.867)
                    })
                    nextButton.disable()
                    Audio.add(this.game, EveryCigPart1Audio.NEXT_TIME, {
                        stopAllAudio: false,
                        onStop: () => nextButton.enable()
                    })
                }
            })
        }
        screen.openScreen(onOpen)
    }

    private createVotingScreen = () => {
        const screen = this.createDropdownScreen()
        const onZoomIn = () => {
            EveryCigVoteScreenOptionContainer.display(
                this.game,
                this.game.add.group(),
                Scale.x(0.573),
                Scale.y(0.065),
                screen,
                this.finalScreen,
                false
            )
            this.buttons.fadeOut()
        }
        screen.addClose(() => {
            this.buttons.fadeIn()
            this.buttons.enable()
            screen.destroy()
        })
        screen.zoomIn(onZoomIn)
    }

    private setContinueButton = () => {
        this.button = Buttons.create(this.game, "Continue", undefined, {
            buttonType: ButtonType.PRIMARY,
            buttonSize: ButtonSize.WIDE,
            scale: 1.1,
            x: Scale.x(0.628),
            y: Scale.y(0.872),
        })
        this.button.disable()
    }

    private createVoteGrossestButton = (onClick: () => void, disable = true) => {
        this.button = Buttons.create(this.game, "Vote for the \"Grossest\" Video", onClick, {
            buttonType: ButtonType.PRIMARY,
            buttonSize: ButtonSize.WIDE,
            scale: 1.1,
            x: Scale.x(0.452),
            y: Scale.y(0.896)
        })
        this.button.button.width = this.button.text.width + Scale.x(0.04)
        this.button.center()
        if (disable) {
            this.button.disable()
        }
    }

    private createDropdownScreen = (): EveryCigDropdownScreenContainer =>
        this.sprites.create(EveryCigSpriteType.VOTE_SCREEN, {
            x: Scale.x(0.491),
            y: Scale.y(-0.542)
        }) as EveryCigDropdownScreenContainer
}

export const initializeEveryCigStateShared = async (state: EveryCigStateShared) => {
    await PhaserUtils.sharedCreateFunctions(state.game)
    ColorUtils.setPageBackgroundColor(COLOR.black)
    ColorUtils.setGameBackgroundColor(state.game, "#aeb079")

    state.sprites = new EveryCigSpriteCreator(state.game)
    Sprites.add(state.game, EveryCigImages.BACKGROUND, {
        x: Scale.x(0.003),
        y: Scale.y(0.017)
    })
}

export interface EveryCigStateShared {
    game: Phaser.Game,
    sprites: EveryCigSpriteCreator
}
